import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLanguageSync } from "@/hooks/useLanguage";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import JsonLd from "@/components/feature/JsonLd";
import { buildArticleSchema, buildBreadcrumbSchema } from "@/data/seo";
import { MAIN_SITE_URL } from "@/data/categories";

const ARTICLE_FAQ = [
  { q: "article.faq.1q", a: "article.faq.1a" },
  { q: "article.faq.2q", a: "article.faq.2a" },
  { q: "article.faq.3q", a: "article.faq.3a" },
  { q: "article.faq.4q", a: "article.faq.4a" },
];

function Section({
  title,
  paragraphs,
}: {
  title: string;
  paragraphs: string[];
}) {
  return (
    <section className="mt-10">
      <h2 className="text-xl md:text-2xl font-heading font-bold">{title}</h2>
      {paragraphs.map((p, i) => (
        <p key={i} className="mt-3 text-foreground-700 leading-relaxed">
          {p}
        </p>
      ))}
    </section>
  );
}

export default function ArticlePage() {
  const { lang = "en" } = useParams();
  const { t } = useTranslation();
  useLanguageSync(lang);
  const [open, setOpen] = useState<number>(0);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [lang]);

  return (
    <>
      <JsonLd
        data={[
          buildArticleSchema({
            lang,
            path: `/${lang}/guide`,
            headline: t("article.title"),
            description: t("article.lead"),
          }),
          buildBreadcrumbSchema({
            lang,
            path: `/${lang}/guide`,
            name: t("nav.guide"),
            homeLabel: t("article.breadcrumb"),
          }),
        ]}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar lang={lang} />
        <main className="pt-32 md:pt-40">
        <div className="w-full px-4 md:px-6 pb-16 md:pb-20">
          <article className="w-full max-w-3xl mx-auto">
            <nav className="text-xs text-foreground-500 flex items-center gap-2 flex-wrap">
              <Link to={`/${lang}`} className="hover:text-primary-600 transition-colors">
                {t("article.breadcrumb")}
              </Link>
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-s-line"></i>
              </span>
              <span>{t("nav.guide")}</span>
            </nav>

            <h1 className="mt-5 text-2xl md:text-4xl font-heading font-extrabold leading-tight">
              {t("article.title")}
            </h1>
            <p className="mt-4 text-sm text-foreground-500 flex items-center gap-2">
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-time-line"></i>
              </span>
              {t("article.meta")}
            </p>

            <p className="mt-6 text-lg text-foreground-700 leading-relaxed">
              {t("article.lead")}
            </p>

            <Section
              title={t("article.what.title")}
              paragraphs={[t("article.what.p1"), t("article.what.p2")]}
            />
            <Section
              title={t("article.how.title")}
              paragraphs={[t("article.how.p1"), t("article.how.p2")]}
            />
            <Section
              title={t("article.categories.title")}
              paragraphs={[t("article.categories.p1")]}
            />
            <Section
              title={t("article.shipping.title")}
              paragraphs={[t("article.shipping.p1"), t("article.shipping.p2")]}
            />
            <Section
              title={t("article.qc.title")}
              paragraphs={[t("article.qc.p1"), t("article.qc.p2")]}
            />
            <Section
              title={t("article.legit.title")}
              paragraphs={[t("article.legit.p1"), t("article.legit.p2")]}
            />
            <Section
              title={t("article.coupon.title")}
              paragraphs={[t("article.coupon.p1"), t("article.coupon.p2")]}
            />
            <Section
              title={t("article.safety.title")}
              paragraphs={[t("article.safety.p1"), t("article.safety.p2")]}
            />

            <section className="mt-10">
              <h2 className="text-xl md:text-2xl font-heading font-bold">
                {t("article.faq.title")}
              </h2>
              <div className="mt-5 space-y-3">
                {ARTICLE_FAQ.map((item, i) => (
                  <div
                    key={item.q}
                    className="rounded-lg bg-background-100 border border-background-200 overflow-hidden"
                  >
                    <button
                      onClick={() => setOpen(open === i ? -1 : i)}
                      className="w-full flex items-center justify-between gap-4 p-4 text-left cursor-pointer"
                    >
                      <span className="font-medium text-foreground-900">{t(item.q)}</span>
                      <span
                        className={`w-6 h-6 flex items-center justify-center shrink-0 text-foreground-500 transition-transform ${
                          open === i ? "rotate-180" : ""
                        }`}
                      >
                        <i className="ri-arrow-down-s-line"></i>
                      </span>
                    </button>
                    {open === i && (
                      <div className="px-4 pb-4 text-sm text-foreground-600 leading-relaxed">
                        {t(item.a)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>

            <section className="mt-10">
              <h2 className="text-xl md:text-2xl font-heading font-bold">
                {t("article.conclusion.title")}
              </h2>
              <p className="mt-3 text-foreground-700 leading-relaxed">
                {t("article.conclusion.p1")}
              </p>
              <p className="mt-3 text-foreground-700 leading-relaxed">
                {t("article.conclusion.p2")}{" "}
                <Link to={`/${lang}`} className="text-primary-600 font-medium hover:underline">
                  {t("article.back")}
                </Link>
                .
              </p>
            </section>

            <div className="mt-10 p-6 md:p-8 rounded-lg bg-primary-50 border border-primary-100">
              <h2 className="text-xl font-heading font-bold">{t("article.cta.title")}</h2>
              <div className="mt-5 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <a
                  href={MAIN_SITE_URL}
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-external-link-line"></i>
                  {t("article.cta.button")}
                </a>
                <Link
                  to={`/${lang}`}
                  className="inline-flex items-center gap-2 text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-left-line"></i>
                  {t("article.back")}
                </Link>
              </div>
            </div>
          </article>
        </div>
      </main>
      <Footer lang={lang} />
      </div>
    </>
  );
}